import { z } from 'zod';
import { accentThemeSchema, colorModeSchema, langSchema, userRoleSchema } from './enums.js';

/** Settings and user-management contracts (docs/spec.md §4.6, §7 screen 10). */

/**
 * Logo is stored as a data URI in `settings` rather than as a file.
 *
 * The printed receipt embeds it inline anyway (docs/spec.md §8.3), so keeping
 * it in the database means one thing to back up instead of two, and no upload
 * volume to lose on a container rebuild. 512 KB is generous for a clinic logo
 * and small enough not to bloat every settings read.
 */
export const LOGO_MAX_BYTES = 512 * 1024;

export const clinicSettingsSchema = z.object({
  'clinic.name.la': z.string().trim().min(1).max(160),
  'clinic.name.en': z.string().trim().max(160),
  'clinic.subtitle.la': z.string().trim().max(200),
  'clinic.subtitle.en': z.string().trim().max(200),
  'clinic.phone': z.string().trim().max(64),
  'clinic.address': z.string().trim().max(255),
  'clinic.issuePlace': z.string().trim().max(120),
});
export type ClinicSettings = z.infer<typeof clinicSettingsSchema>;

export const systemSettingsSchema = z.object({
  /** Invoice totals round to this (docs/spec.md §4.7). */
  'billing.roundingUnit': z.coerce.number().int().min(1).max(100_000),
  'inventory.expiryWarnDays': z.coerce.number().int().min(1).max(3650),
  'ui.defaultLang': langSchema,
  'ui.defaultTheme': accentThemeSchema,
  'ui.defaultMode': colorModeSchema,
  /**
   * Idle timeout, in hours — a session with no requests for this long is
   * treated as abandoned. Sliding: any request resets the clock, so an
   * active shift never gets kicked (docs/spec.md §14 round 3, E12).
   * 1–720h (30 days); default 24h, seeded in db/seed.ts.
   */
  'session.ttlHours': z.coerce.number().min(1).max(720),
  /**
   * Kip per 1 unit of the foreign currency — how invoices/sales convert a
   * total for display when the customer is quoted in THB or USD instead of
   * kip (docs/spec.md §14 round 4, G7). The ledger itself always stays in
   * kip; these only drive the printed/on-screen conversion.
   */
  'fx.THB': z.coerce.number().min(0.0001).max(1_000_000),
  'fx.USD': z.coerce.number().min(0.0001).max(1_000_000),
});
export type SystemSettings = z.infer<typeof systemSettingsSchema>;

export const settingsUpdateSchema = clinicSettingsSchema
  .partial()
  .merge(systemSettingsSchema.partial())
  .extend({
    'clinic.logoDataUri': z
      .string()
      .max(LOGO_MAX_BYTES * 2)
      .refine((value) => value === '' || value.startsWith('data:image/'), 'image_required')
      .optional(),
  });
export type SettingsUpdate = z.infer<typeof settingsUpdateSchema>;

export type SettingsMap = Record<string, unknown>;

/* ---- Per-user preferences ---- */

export const userPrefsSchema = z.object({
  lang: langSchema,
  accentTheme: accentThemeSchema,
  colorMode: colorModeSchema,
});
export type UserPrefs = z.infer<typeof userPrefsSchema>;

/* ---- User management (admin) ---- */

export const userCreateSchema = z.object({
  username: z
    .string()
    .trim()
    .min(3)
    .max(64)
    .regex(/^[a-zA-Z0-9._-]+$/, 'username_format'),
  fullName: z.string().trim().min(1).max(160),
  role: userRoleSchema,
  phone: z.string().trim().max(32).optional().or(z.literal('')),
  /** 10 characters minimum — this is a shared clinic terminal, not a personal laptop. */
  password: z.string().min(10).max(200),
});
export type UserCreateInput = z.infer<typeof userCreateSchema>;

export const userUpdateSchema = z.object({
  fullName: z.string().trim().min(1).max(160).optional(),
  role: userRoleSchema.optional(),
  phone: z.string().trim().max(32).optional().or(z.literal('')),
  isActive: z.boolean().optional(),
});
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

export const passwordResetSchema = z.object({
  password: z.string().min(10).max(200),
});

export const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(10).max(200),
});

export interface ManagedUser {
  id: number;
  username: string;
  fullName: string;
  role: 'admin' | 'doctor' | 'reception' | 'lab';
  phone: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/** Lab tests the clinic pre-ticks on a new order (docs/spec.md §4.5.1). */
export const labDefaultsSchema = z.object({
  codes: z.array(z.string().trim().min(1).max(48)),
});
export type LabDefaultsInput = z.infer<typeof labDefaultsSchema>;
